import React from 'react';
import { MenuIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/contexts/SidebarContext';
import { GuideLauncher } from '@/features/guided-help';
import { useAuth } from '@/hooks/useAuth';

const Header: React.FC = () => {
  const { toggleMobile, toggleDesktop } = useSidebar();
  const { user } = useAuth();

  // Function to get user initials from a single name string
  const getUserInitials = (fullName: string | undefined) => {
    if (!fullName) return 'U';
    const parts = fullName.split(' ').filter(Boolean);
    if (parts.length === 0) return 'U';

    const firstInitial = parts[0].charAt(0).toUpperCase();
    if (parts.length === 1) return firstInitial;

    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  const userInitials = getUserInitials(user?.name);
  const displayUserName = user?.name || 'Usuario';

  return (
    <header className="fixed left-0 right-0 top-0 z-20 h-16 border-b border-gray-200 bg-white px-6 shadow-sm">
      <div className="relative flex h-full items-center justify-between">
        {/* Left side - Menu button */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobile}
            className="text-gray-600 hover:text-gray-900 lg:hidden"
          >
            <MenuIcon className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDesktop}
            className="hidden text-gray-600 hover:text-gray-900 lg:flex"
          >
            <MenuIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Center - Logo (absolute positioned for perfect centering) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {/* Desktop - Full logo */}
          <img
            src="/assets/images/logo.svg"
            alt="GetMatch Logo"
            className="hidden h-8 w-auto lg:block"
          />
          {/* Mobile - Isotipo */}
          <img
            src="/assets/images/isotipo.svg"
            alt="GetMatch Isotipo"
            className="h-8 w-auto lg:hidden"
          />
        </div>

        {/* Right side - User info */}
        <div className="flex items-center space-x-3">
          <GuideLauncher />
          <p className="hidden text-sm font-medium text-gray-900 sm:block">{displayUserName}</p>
          {user?.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt={displayUserName}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700">
              {userInitials}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
